import { useMemo, useState, useRef, useContext } from "react";
import DOMPurify from "dompurify";
import { AppStateContext } from "../../state/AppProvider";

import { ChatResponse, getCitationFilePath, updateFeedback } from "../../api";
import { parseAnswerToHtml } from "./AnswerParser";
import { AnswerIcon } from "./AnswerIcon";
import { ButtonTooltip } from "../ButtonTooltip";

interface Props {
    answer: ChatResponse;
    isStreaming: boolean;
    onCitationClicked: (filePath: string, citationTitle: string) => void;
    onCopyAnswerClicked: () => void;
    lastAnswer: boolean;
    commentInput: string;
    setCommentInput: (commentInput: string) => void;
    windowWidth: number;
}

export const Answer = ({ answer, isStreaming, onCitationClicked, onCopyAnswerClicked, lastAnswer, commentInput, setCommentInput, windowWidth }: Props) => {
    const appStateContext = useContext(AppStateContext);
    const messageContent = answer.choices[0].message.content;
    const parsedAnswer = useMemo(() => parseAnswerToHtml(messageContent, isStreaming, onCitationClicked), [answer]);
    const messageId = answer.choices[0].message_id;
    const feedback = answer.choices[0].feedback;
    const feedbackComment = answer.choices[0].feedback_comment;

    const sanitizedAnswerHtml = DOMPurify.sanitize(parsedAnswer.answerHtml);

    const [copyIcon, setCopyIcon] = useState<string>("content_copy");

    let streamingDot: string =
        isStreaming && lastAnswer
            ? " <span class='material-symbols-outlined text-j-dark text-icon12' style=\"font-variation-settings: 'FILL' 1;\"> circle</span>"
            : "";

    function onCopyClicked() {
        onCopyAnswerClicked();
        setCopyIcon("check");
        setTimeout(() => {
            setCopyIcon("content_copy");
        }, 2000);
    }

    const textAreaRef = useRef<HTMLTextAreaElement>(null);

    const calculateRows = (): void => {
        const textArea = textAreaRef.current;
        if (textArea) {
            textArea.style.height = "auto";
            const scrollHeight = textArea.scrollHeight;
            textArea.style.height = `${scrollHeight}px`;
        }
    };

    const sendCommentDisabled = !commentInput.trim();

    const updateMessageFeedback = async (feedback: string | null) => {
        await updateFeedback({
            message_id: messageId || "",
            feedback: feedback
        }).then(res => {
            if (res) {
                appStateContext?.dispatch({ type: "UPDATE_CHAT_HISTORY_MESSAGE", payload: res });
            }
        });
    };

    const updateMessageFeedbackComment = async (comment: string | null) => {
        await updateFeedback({
            message_id: messageId || "",
            feedback: comment,
            feedback_type: "comment"
        }).then(res => {
            if (res) {
                appStateContext?.dispatch({ type: "UPDATE_CHAT_HISTORY_MESSAGE", payload: res });
            }
        });
    };

    const onFeedbackHelpful = () => {
        if (feedback === "Unhelpful") {
            updateMessageFeedback("Helpful");
        } else if (feedback === "Helpful") {
            updateMessageFeedback(null);
        } else {
            updateMessageFeedback("Helpful");
        }
    };

    const onFeedbackUnhelpful = () => {
        if (feedback === "Helpful") {
            updateMessageFeedback("Unhelpful");
        } else if (feedback === "Unhelpful") {
            updateMessageFeedback(null);
        } else {
            updateMessageFeedback("Unhelpful");
        }
    };

    const onCommentChange = (ev: React.ChangeEvent<HTMLTextAreaElement>) => {
        calculateRows();
        if (!ev.target.value) {
            setCommentInput("");
        } else {
            setCommentInput(ev.target.value);
        }
    };

    const sendComment = () => {
        if (!commentInput.trim()) {
            return;
        }
        updateMessageFeedbackComment(commentInput);
    };

    const onEnterPress = (ev: React.KeyboardEvent<Element>) => {
        if (ev.key === "Enter" && !ev.shiftKey) {
            ev.preventDefault();
            sendComment();
        }
    };

    return (
        <div className="w-full">
            <div className="transition-[padding] duration-200 py-[18px] px-4 text-base m-auto">
                <div className="mx-auto flex flex-1 text-base gap-4 lg:gap-6 lg:max-w-3xl">
                    <div className="flex-shrink-0 flex flex-col relative items-end">
                        <div>
                            <div className="pt-0">
                                <div className="relative p-1 flex items-center justify-center h-9 w-9 lg:h-16 lg:w-16">
                                    <AnswerIcon />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="relative flex w-full min-w-0 flex-col rounded-xl bg-j-white px-6 py-8 text-j-dark shadow-[0px_8px_16px_rgba(0,0,0,0.14),0px_0px_2px_rgba(0,0,0,0.12)]">
                        <div className="flex-col gap-1 lg:gap-3">
                            <div className="flex flex-grow flex-col max-w-full">
                                <div className="flex flex-col">
                                    <div className="flex flex-grow flex-col max-w-full">
                                        <div className="min-h-[20px] text-message flex flex-col whitespace-pre-wrap break-words w-full items-end overflow-x-auto gap-2">
                                            <div className="flex w-full flex-col gap-1">
                                                <div className="w-full break-words">
                                                    <div
                                                        className="chat-stream stream-table text-base leading-[1.75]"
                                                        dangerouslySetInnerHTML={{ __html: sanitizedAnswerHtml + streamingDot }}
                                                    ></div>
                                                    {(!isStreaming || !lastAnswer) && (
                                                        <>
                                                            <div className="ml-1 flex">
                                                                <ButtonTooltip title="Copy" placement="top">
                                                                    <button
                                                                        className="flex justify-center font-semibold text-j-green p-0 mt-[-8px]"
                                                                        onClick={() => onCopyClicked()}
                                                                    >
                                                                        <span className="material-symbols-outlined text-[1.25rem]">{copyIcon}</span>
                                                                    </button>
                                                                </ButtonTooltip>
                                                            </div>
                                                        </>
                                                    )}
                                                    {parsedAnswer.citations.length > 0 && (
                                                        <div className="">
                                                            <hr className="border-t-2 border-j-dark/30 my-8" />
                                                            <div className="font-bold pb-2">Sources:</div>
                                                            <div className="flex flex-wrap gap-2">
                                                                {parsedAnswer.citations.map((x, i) => {
                                                                    let citation = x.replace(/#page_label=(\d+)/, "");
                                                                    let title = x.replace(/\.\w+#page=(\d+)/, "");
                                                                    title = title.replace(/#page_label=/, " - Page ");
                                                                    let citationTitle = x.replace(/\.\w+#page=.*$/, "");

                                                                    const path = getCitationFilePath(citation);
                                                                    return (
                                                                        <div className="flex">
                                                                            <span className="flex items-center justify-center min-w-[2rem] h-8 w-8 rounded-full bg-j-dark text-j-white text-sm font-bold">
                                                                                {i + 1}
                                                                            </span>
                                                                            <a
                                                                                key={i}
                                                                                className="flex items-center justify-center pl-2 text-j-green font-bold hover:underline cursor-pointer"
                                                                                title={title}
                                                                                onClick={() => onCitationClicked(path, citationTitle)}
                                                                            >{`${title}`}</a>
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>
                                                        </div>
                                                    )}
                                                    {(!isStreaming || !lastAnswer) && <hr className="border-t-2 border-j-dark/30 my-8" />}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    {messageId && (
                                        <div className="flex flex-col">
                                            <div className={feedbackComment ? "" : "hidden"}>
                                                <span className="font-bold">Thank you for your feedback!</span>
                                            </div>
                                            <div className={feedbackComment || (isStreaming && lastAnswer) ? "hidden" : "flex"}>
                                                <span className="font-bold">Is this answer helpful?</span>
                                                <span className="ml-3 flex gap-3">
                                                    <ButtonTooltip title="Helpful">
                                                        <button
                                                            className={
                                                                "flex justify-center font-semibold text-j-green p-0 pb-[3px]" +
                                                                (feedback === "Helpful" ? " icon-underline" : "")
                                                            }
                                                            onClick={onFeedbackHelpful}
                                                        >
                                                            <span className="material-symbols-outlined text-[1.25rem]">thumb_up</span>
                                                        </button>
                                                    </ButtonTooltip>
                                                    <ButtonTooltip title="Unhelpful">
                                                        <button
                                                            className={
                                                                "flex justify-center font-semibold text-j-green p-0 pb-[3px]" +
                                                                (feedback === "Unhelpful" ? " icon-underline" : "")
                                                            }
                                                            onClick={onFeedbackUnhelpful}
                                                        >
                                                            <span className="material-symbols-outlined text-[1.25rem]">thumb_down</span>
                                                        </button>
                                                    </ButtonTooltip>
                                                </span>
                                            </div>
                                            <div className={"w-full pt-4 border-transparent " + (feedback && !feedbackComment ? "" : "hidden")}>
                                                <div className="flex w-full items-center">
                                                    <div className="overflow-hidden flex flex-col w-full flex-grow relative rounded-xl bg-j-white shadow-[0px_8px_16px_rgba(0,0,0,0.14),0px_0px_2px_rgba(0,0,0,0.12)]">
                                                        <textarea
                                                            ref={textAreaRef}
                                                            className="m-0 w-full resize-none border-0 bg-transparent focus-visible:outline-0 focus:ring-0 focus-visible:ring-0 py-3.5 pr-[3.75rem] placeholder-j-light-grey max-h-[200px] min-h-[52px] pl-4"
                                                            placeholder={windowWidth < 640 ? "Provide feedback" : "Please provide feedback"}
                                                            rows={1}
                                                            value={commentInput}
                                                            onChange={onCommentChange}
                                                            onKeyDown={onEnterPress}
                                                        ></textarea>
                                                        <div
                                                            className={
                                                                "absolute bottom-0 right-0 h-full w-[52px] rounded-r-xl flex justify-center items-center" +
                                                                (sendCommentDisabled
                                                                    ? " bg-j-super-light-grey cursor-default"
                                                                    : " bg-gradient-to-r from-j-green to-j-cyan cursor-pointer hover:from-j-green-hover hover:to-j-cyan-hover")
                                                            }
                                                        >
                                                            <a
                                                                className={
                                                                    "text-5xl material-symbols-outlined" +
                                                                    (sendCommentDisabled ? " text-j-light-grey cursor-default" : " text-j-dark cursor-pointer")
                                                                }
                                                                onClick={sendComment}
                                                            >
                                                                arrow_upward_alt
                                                            </a>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
